import React, { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { BookOpenIcon, ChevronRightIcon, SparklesIcon } from 'lucide-react';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import UnifiedBookCard from '../components/Books/UnifiedBookCard';
import { PopularContentTags } from '../components/Genres/PopularContentTags';
import { allBooks, type BookCatalogEntry } from '../data/booksData';
import { genreGlossaryData } from '../data/genreGlossaryData';
import { getSubgenreBySlug, toTaxonomySlug } from '../data/subgenreRegistry';
import { getSubgenreContentTags, type SubgenreContentTag } from '../data/subgenreContentTags';

/**
 * Every searchable text fragment attached to a book. Used to match content tags
 * against books without needing a dedicated join table in the prototype.
 */
const bookTagCorpus = (book: BookCatalogEntry): string[] => [...(book.tropes || []), ...(book.tags || []), ...(book.contentWarnings || []), ...(book.subgenres || []), ...(book.genres || []), ...(book.refinements || []), book.mainSubgenre, book.displayLabel, book.settingId, book.pairingType].filter((v): v is string => typeof v === 'string').map(v => v.toLowerCase());
const bookMatchesTag = (book: BookCatalogEntry, tag: SubgenreContentTag) => {
  const needle = tag.name.toLowerCase();
  return bookTagCorpus(book).some(value => value.includes(needle) || needle.includes(value));
};

/**
 * Subgenre landing page — steps two through four of the drill-down.
 *
 * Content tags here filter WITHIN this subgenre only. Selecting "Hockey" on
 * Sports Romance narrows Sports Romance books; it never escapes to a global
 * tag search. Global search stays available on /all-content-tags.
 */
const SubgenrePage = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    genreSlug,
    subgenreSlug
  } = useParams<{
    genreSlug: string;
    subgenreSlug: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const resolved = useMemo(() => subgenreSlug ? getSubgenreBySlug(subgenreSlug) : undefined, [subgenreSlug]);
  const glossaryGenre = useMemo(() => genreGlossaryData.find(g => g.slug === genreSlug || g.name === resolved?.genre), [genreSlug, resolved]);
  const glossarySubgenre = useMemo(() => glossaryGenre?.subgenres.find(s => toTaxonomySlug(s.name) === subgenreSlug), [glossaryGenre, subgenreSlug]);
  const tags = useMemo(() => subgenreSlug ? getSubgenreContentTags(subgenreSlug) : [], [subgenreSlug]);

  // Selected tags live in the URL so a filtered view is shareable.
  const selectedTagIds = useMemo(() => {
    const raw = searchParams.get('tags');
    return raw ? raw.split(',').filter(Boolean) : [];
  }, [searchParams]);
  const setSelectedTagIds = (ids: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (ids.length > 0) next.set('tags', ids.join(','));else next.delete('tags');
    setSearchParams(next, {
      replace: true
    });
  };
  const toggleTag = (tagId: string) => setSelectedTagIds(selectedTagIds.includes(tagId) ? selectedTagIds.filter(id => id !== tagId) : [...selectedTagIds, tagId]);
  const label = resolved?.entry.displayLabel || glossarySubgenre?.name || '';

  /** Books belonging to this subgenre — the bounded universe for this page. */
  const subgenreBooks = useMemo(() => {
    if (!label) return [];
    const needle = label.toLowerCase();
    return allBooks.filter(book => {
      const fields = [book.mainSubgenre, book.displayLabel, ...(book.subgenres || []), ...(book.genres || [])].filter((v): v is string => typeof v === 'string').map(v => v.toLowerCase());
      return fields.some(v => v === needle || v.includes(needle));
    });
  }, [label]);
  const selectedTags = tags.filter(t => selectedTagIds.includes(t.id));

  /** Contextual filter: every selected tag must match (AND). */
  const filteredBooks = useMemo(() => {
    if (selectedTags.length === 0) return subgenreBooks;
    return subgenreBooks.filter(book => selectedTags.every(tag => bookMatchesTag(book, tag)));
  }, [subgenreBooks, selectedTags]);

  /** Per-tag counts within this subgenre, so numbers reflect the context. */
  const resultCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const tag of tags) {
      counts[tag.id] = subgenreBooks.filter(book => bookMatchesTag(book, tag)).length;
    }
    return counts;
  }, [tags, subgenreBooks]);
  const featuredBooks = useMemo(() => [...subgenreBooks].sort((a, b) => b.rating - a.rating).slice(0, 5), [subgenreBooks]);
  if (!resolved && !glossarySubgenre) {
    return <div className={`min-h-screen w-full ${dk.page}`} data-id="element-14243" data-mp="3li35wzvxjl9i">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-id="element-14244" data-mp="3t1m9yu0fefal">
          <BookOpenIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-14245" data-mp="1vuhvp338sl5" />
          <h1 className={`text-2xl font-bold mb-2 ${dk.textHeading}`} data-id="element-14246" data-mp="1trnytytefb5">
            Subgenre not found
          </h1>
          <Link to="/genres" className={`font-medium ${rt.accentLink}`} data-id="element-14247" data-mp="3o32nmshx71hp">
            Browse all genres
          </Link>
        </div>
      </div>;
  }
  const description = glossarySubgenre?.definition || `Browse ${label} titles across the LitLaunch catalogue.`;
  const bookCount = glossarySubgenre?.bookCount ?? subgenreBooks.length;
  return <div className={`min-h-screen w-full ${dk.page}`} data-id="element-14248" data-mp="5wwm6kbv3utz">
      {/* Header */}
      <div className={`py-6 px-4 ${dk.surface}`} data-id="element-14249" data-mp="avhb3nlnys34">
        <div className="max-w-7xl mx-auto" data-id="element-14250" data-mp="au67pow4rmcj">
          <nav aria-label="Breadcrumb" className={`flex flex-wrap items-center text-sm mb-4 ${dk.textMuted}`} data-id="element-14251" data-mp="ylv8zuqxjl5m">
            <Link to="/" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-14252" data-mp="13ewp262vhqqs">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-14253" data-mp="189pk8f4g3hki" />
            <Link to="/genres" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-14254" data-mp="18a8t76c4avme">
              Genres
            </Link>
            {glossaryGenre && <>
                <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-14255" data-mp="zb5dmcc5sycn" />
                <Link to={`/genres/${glossaryGenre.slug}`} className={`hover:${dk.textPrimary} transition-colors`} data-id="element-14256" data-mp="uapz3hg55kt3">
                  {glossaryGenre.name}
                </Link>
              </>}
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-14257" data-mp="git9bk1y3mzv" />
            <span className={dk.textPrimary} data-id="element-14258" data-mp="gke51czzxbzb">{label}</span>
            {selectedTags.length > 0 && <>
                <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-14259" data-mp="blazlrg3t3qt" />
                <span className={dk.textPrimary} data-id="element-14260" data-mp="6loj994zb7ds">
                  {selectedTags.map(t => t.name).join(' + ')}
                </span>
              </>}
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-2" data-id="element-14261" data-mp="2n60kunqeya7w">
            <h1 className={`text-2xl md:text-3xl font-bold ${dk.textHeading}`} data-id="element-14262" data-mp="2s5wqssio8bq3">
              {label}
            </h1>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${dk.badgeNeutral}`} data-id="element-14263" data-mp="2w804zowq5uf2">
              {bookCount.toLocaleString()} books
            </span>
          </div>
          <p className={`text-base leading-relaxed max-w-3xl ${dk.textSecondary}`} data-id="element-14264" data-mp="24h3pshn6zdom">
            {description}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-14265" data-mp="2d8vzgq4e6is5">
        {/* Featured books — hidden once the reader starts filtering, so the
            results below become the single focus. */}
        {featuredBooks.length > 0 && selectedTags.length === 0 && <section aria-labelledby="featured-books" className="mb-10" data-id="element-14266" data-mp="e50rws7wqyj2">
            <div className="flex items-center gap-2 mb-4" data-id="element-14267" data-mp="e594ej04c1tq">
              <SparklesIcon className={`h-5 w-5 ${rt.textPrimary}`} data-id="element-14268" data-mp="96pjmlx9gw2y" />
              <h2 id="featured-books" className={`text-lg font-bold ${dk.textHeading}`} data-id="element-14269" data-mp="o36p4wirrghl">
                Featured in {label}
              </h2>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1" data-id="element-14270" data-mp="x3lgodmbtrtj">
              {featuredBooks.map(book => <div key={book.id} className="w-40 sm:w-44 flex-shrink-0" data-id="element-14271" data-mp="s5jtphls58cs">
                  <UnifiedBookCard book={book} data-id="element-14272" data-mp="16tepx7yzxuum" />
                </div>)}
            </div>
          </section>}

        <PopularContentTags tags={tags} selectedTagIds={selectedTagIds} onToggleTag={toggleTag} onClearTags={() => setSelectedTagIds([])} resultCounts={resultCounts} subgenreName={label} data-id="element-14273" data-mp="11vmqztzxp8gx" />

        {/* Book results */}
        <section aria-labelledby="book-results" data-id="element-14274" data-mp="1ugthc2fe1e9g">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4" data-id="element-14275" data-mp="1ueyxkrfurs6y">
            <h2 id="book-results" className={`text-lg font-bold ${dk.textHeading}`} data-id="element-14276" data-mp="1zfd4251t2u2v">
              {selectedTags.length > 0 ? `${label} · ${selectedTags.map(t => t.name).join(' + ')}` : `All ${label}`}
            </h2>
            <p className={`text-sm ${dk.textSecondary}`} aria-live="polite" data-id="element-14277" data-mp="1bp0kfwx778wg">
              {filteredBooks.length}{' '}
              {filteredBooks.length === 1 ? 'book' : 'books'}
            </p>
          </div>

          {filteredBooks.length === 0 ? <div className={`text-center py-16 rounded-xl border border-dashed ${dk.border} ${dk.surface}`} data-id="element-14278" data-mp="16qz61papvxrb">
              <BookOpenIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-14279" data-mp="ss7sq53oig48" />
              <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-14280" data-mp="xq12170a3q8q">
                No {label} books match these tags
              </h3>
              <p className={`mb-4 ${dk.textSecondary}`} data-id="element-14281" data-mp="1kj44baj8opxb">
                Try removing a tag to widen your results.
              </p>
              {selectedTags.length > 0 && <button onClick={() => setSelectedTagIds([])} className={`font-medium ${rt.accentLink}`} data-id="element-14282" data-mp="1bhwivt1tnjkn">
                  Clear all tags
                </button>}
            </div> : <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6" data-id="element-14283" data-mp="245sbalkcypbm">
              {filteredBooks.map(book => <UnifiedBookCard key={book.id} book={book} data-id="element-14284" data-mp="1ug8pb71cmfcj" />)}
            </div>}
        </section>
      </div>
    </div>;
};
export default SubgenrePage;